import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateCustomFieldInput,
  CustomFieldDefinition,
  CustomFieldEntityType,
  CustomFieldValue,
  SetCustomFieldValuesInput,
  UpdateCustomFieldInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['custom-fields'] as const;

function toQuery(
  entityType?: CustomFieldEntityType,
  includeInactive?: boolean,
): Record<string, string | undefined> {
  return {
    entityType,
    includeInactive: includeInactive ? 'true' : undefined,
  };
}

export const listCustomFields = (entityType?: CustomFieldEntityType, includeInactive?: boolean) =>
  apiClient.get<CustomFieldDefinition[]>('/custom-fields', {
    query: toQuery(entityType, includeInactive),
  });
export const createCustomField = (input: CreateCustomFieldInput) =>
  apiClient.post<CustomFieldDefinition>('/custom-fields', input);
export const updateCustomField = (id: string, patch: UpdateCustomFieldInput) =>
  apiClient.patch<CustomFieldDefinition>(`/custom-fields/${id}`, patch);
export const deleteCustomField = (id: string) => apiClient.delete<void>(`/custom-fields/${id}`);

export const setContactCustomFields = (contactId: string, input: SetCustomFieldValuesInput) =>
  apiClient.put<CustomFieldValue[]>(`/contacts/${contactId}/custom-fields`, input);
export const setLeadCustomFields = (leadId: string, input: SetCustomFieldValuesInput) =>
  apiClient.put<CustomFieldValue[]>(`/leads/${leadId}/custom-fields`, input);

export function useCustomFieldDefinitions(
  entityType?: CustomFieldEntityType,
  includeInactive = false,
) {
  return useQuery<CustomFieldDefinition[], ApiClientError>({
    queryKey: [...KEY, 'list', entityType ?? 'ALL', includeInactive],
    queryFn: () => listCustomFields(entityType, includeInactive),
  });
}

export function useCreateCustomField() {
  const qc = useQueryClient();
  return useMutation<CustomFieldDefinition, ApiClientError, CreateCustomFieldInput>({
    mutationFn: createCustomField,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateCustomField() {
  const qc = useQueryClient();
  return useMutation<
    CustomFieldDefinition,
    ApiClientError,
    { id: string; patch: UpdateCustomFieldInput }
  >({
    mutationFn: ({ id, patch }) => updateCustomField(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteCustomField() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: deleteCustomField,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useSetContactCustomFields(contactId: string) {
  const qc = useQueryClient();
  return useMutation<CustomFieldValue[], ApiClientError, SetCustomFieldValuesInput>({
    mutationFn: (input) => setContactCustomFields(contactId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts'] }),
  });
}

export function useSetLeadCustomFields(leadId: string) {
  const qc = useQueryClient();
  return useMutation<CustomFieldValue[], ApiClientError, SetCustomFieldValuesInput>({
    mutationFn: (input) => setLeadCustomFields(leadId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });
}
